import React, { useCallback, useEffect, useMemo } from 'react';
import { Button, Form, Input, Modal, Select, TreeSelect } from 'antd';
import { CloseOutlined } from '@ant-design/icons';
import './styleDeviceGroup.css';
import { useAuthStore } from '../../../store/auth/auth';
import { getCreateDevice } from '../../../api/devices/getCreateDevice';
import { useGroupsStore } from '../../../store/groups/Groups';
import { Group } from '../../../types/Group';
import { useIsDeviceAdded } from '../../../store/devices/isDeviceAdded';
import { message } from 'antd';
import { DeviceByGroupStore } from '../../../store/devices/DeviceByGroupStore';
import { useOwnerStore } from '../../../store/owner/useOwnerStore';

const { Option } = Select;
interface NewGroupModalProps {
    visible: boolean;
    onOk: () => void;
    onCancel: () => void;
}
const NewDeviceModal: React.FC<NewGroupModalProps> = ({ visible, onOk, onCancel }) => {
    const [form] = Form.useForm();
    const { user, SmartDVRToken } = useAuthStore();
    const { groups, fetchGroups } = useGroupsStore();
    const { setIsDeviceAdded } = useIsDeviceAdded();
    const { devicesByStore, fetchDevicesByStore } = DeviceByGroupStore();
    const { owners, fetchOwners } = useOwnerStore();

    const handleCancel = () => {
        form.resetFields();
        onCancel();
    };

    const handleNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value;
        const numericValue = value.replace(/[^0-9]/g, '');

        if (value !== numericValue) {
            message.error('Название устройства может содержать только цифры!');
        }

        form.setFieldsValue({ name: numericValue });
    };

    const getAllSubGroups = useCallback((group: Group): Group[] => {
        let subGroups: Group[] = [];
        group?.sub_groups.forEach((subGroup) => {
            if (subGroup) {
                subGroups.push(subGroup);
                if (subGroup.sub_groups?.length > 0) {
                    subGroups = [...subGroups, ...getAllSubGroups(subGroup)];
                }
            }
        });
        return subGroups;
    }, []);

    const allGroups = useMemo(() => {
        let result: Group[] = [];
        groups.forEach((group) => {
            result.push(group);
            if (group.sub_groups) {
                result = [...result, ...getAllSubGroups(group)];
            }
        });
        return result;
    }, [groups, getAllSubGroups]);

    const convertGroupsToTreeData = useCallback((groups: Group[]): any[] => {
        return groups
            .filter((group) =>
                group.uid !== '00000000-0000-0000-0000-000000000002' &&
                group.uid !== '00000000-0000-0000-0000-000000000003' &&
                group.name !== 'Несортированные'
            )
            .map((group) => ({
                title: group.name,
                value: group.uid,
                key: group.uid,
                children: group.sub_groups ? convertGroupsToTreeData(group.sub_groups) : undefined,
            }));
    }, []);

    const treeData = useMemo(() => {
        const rootGroup = groups.find(group => group.name === 'Root');
        if (rootGroup && rootGroup.sub_groups) {
            return convertGroupsToTreeData(rootGroup.sub_groups);
        }
        return convertGroupsToTreeData(groups);
    }, [groups, convertGroupsToTreeData]);

    useEffect(() => {
        if (visible) {
            if (groups.length === 0) {
                fetchGroups();
            }
            fetchOwners(1, 1000);
            form.resetFields();
        }
    }, [visible, groups.length, fetchOwners, form]);

    const handleOk = async () => {
        try {
            const values = await form.validateFields();

            if (!values.name.trim()) {
                message.error('Введите название устройства!');
                return;
            }
            if (!/^\d+$/.test(values.name.trim())) {
                message.error('Название устройства может содержать только цифры!');
                return;
            }
            if (!values.DID.trim()) {
                message.error('Введите номер устройства!');
                return;
            }
            if (!values.groupUID) {
                values.groupUID = '00000000-0000-0000-0000-000000000002';
            }
            if (!user) {
                message.error('Ошибка авторизации!');
                return;
            }

            const response = await getCreateDevice(SmartDVRToken, user.login, values);

            if (response.success) {
                message.success('Устройство успешно добавлено!');
                setIsDeviceAdded(true);
                form.resetFields();
                onOk();
            } else {
                message.error(`Ошибка: ${response.error}`);
            }
        } catch (errorInfo) {
            console.error('Validation failed:', errorInfo);
        }
    };

    return (
        <Modal
            title={
                <div className="titleModal_Device">
                    <span className="spanDevice">Добавить устройство</span>
                    <CloseOutlined onClick={handleCancel} className="closeBut_Device" />
                </div>
            }
            className="newGroup_modal"
            visible={visible}
            onOk={onOk}
            onCancel={handleCancel}
            width={720}
            closable={false}
            footer={
                <Button className="buttonAdd" onClick={handleOk}>
                    Добавить
                </Button>
            }
        >
            <Form form={form} layout="horizontal" className="form_dev">
                <Form.Item
                    className="form_name_Device"
                    label={<span className="inputLabel_Device">Название</span>}
                    name="name"
                    required={false}
                >
                    <Input
                        className="input_Group"
                        onChange={handleNameChange}
                        placeholder="Введите только цифры (Обязательно)"
                    />
                </Form.Item>
                <Form.Item
                    className="form_Description_Device"
                    label={<span className="inputLabel_Device">Описание</span>}
                    name="description"
                    required={false}
                >
                    <Input className="input_Device" />
                </Form.Item>
                <Form.Item
                    className="form_Group"
                    label={<span className="inputLabel_Group">Группа</span>}
                    name="groupUID"
                    required={false}
                >
                    <TreeSelect
                        className="selectGroup"
                        placeholder="Введите название или выберите из списка"
                        treeData={treeData}
                        showSearch
                        allowClear
                        treeDefaultExpandAll
                        filterTreeNode={(inputValue, treeNode) => {
                            const title = String(treeNode.title || '');
                            return title.toLowerCase().includes(inputValue.toLowerCase());
                        }}
                        treeNodeFilterProp="title"
                    />
                </Form.Item>
                <Form.Item
                    className="form_Employee_Device"
                    label={<span className="inputLabel_Device">Владелец</span>}
                    name="owner"
                    required={false}
                >
                    <Select
                        className="input_Device"
                        placeholder="Выберите владельца или введите для поиска"
                        showSearch
                        allowClear
                        filterOption={(input, option) => {
                            const children = Array.isArray(option?.children)
                                ? option?.children[0]
                                : option?.children;
                            return String(children || '').toLowerCase().includes(input.toLowerCase());
                        }}
                    >
                        {owners.map((owner) => (
                            <Option key={owner.UID} value={owner.UID}>
                                {owner.name}
                            </Option>
                        ))}
                    </Select>
                </Form.Item>
                <Form.Item
                    className="form_SerialNumber_Device"
                    label={<span className="inputLabel_Device">Номер</span>}
                    name="DID"
                    required={false}
                >
                    <Input className="input_Device" placeholder="(Обязательно)" />
                </Form.Item>
                <Form.Item
                    className="form_Model_Device"
                    label={<span className="inputLabel_Device">Модель</span>}
                    name="model"
                    required={false}
                >
                    <Input className="input_Device" />
                </Form.Item>
            </Form>
        </Modal>
    );
};

export default NewDeviceModal;
